import { Button } from "@/components/ui/button"
import { motion } from "framer-motion"
import {
  SortAsc,
  SortDesc,
  FolderOpen,
  FileText,
  Pin,
  LayoutGrid,
  CheckSquare,
  Archive
} from "lucide-react"
import { useState, useEffect } from "react"

export function FilterBar({ activeFilter, onFilterChange, viewMode, isSelectionMode, onToggleSelectionMode }) {
  const [sortDirection, setSortDirection] = useState('a-z')
  
  // Update sort direction when activeFilter changes externally
  useEffect(() => {
    if (activeFilter === 'a-z' || activeFilter === 'z-a') {
      setSortDirection(activeFilter)
    }
  }, [activeFilter])
  
  const handleSortClick = () => {
    // If currently on a sort filter (a-z or z-a), toggle it
    // If coming from another filter, start with a-z
    if (activeFilter === 'a-z' || activeFilter === 'z-a') {
      const newDirection = sortDirection === 'a-z' ? 'z-a' : 'a-z'
      setSortDirection(newDirection)
      onFilterChange(newDirection)
    } else {
      // Coming from a different filter - default to a-z
      setSortDirection('a-z')
      onFilterChange('a-z')
    }
  }
  
  // Build filters array based on viewMode
  const allFilters = [
    { id: 'all', label: 'All', icon: LayoutGrid, onClick: () => onFilterChange('all') },
    {
      id: 'sort',
      label: sortDirection === 'a-z' ? 'A-Z' : 'Z-A',
      icon: sortDirection === 'a-z' ? SortAsc : SortDesc,
      onClick: handleSortClick,
      isActive: activeFilter === 'a-z' || activeFilter === 'z-a'
    },
    { id: 'projects', label: 'Projects', icon: FileText, onClick: () => onFilterChange('projects') },
    { id: 'stacks', label: 'Stacks', icon: FolderOpen, onClick: () => onFilterChange('stacks') },
    { id: 'pinned', label: 'Pinned', icon: Pin, onClick: () => onFilterChange('pinned') },
    { id: 'archived', label: 'Archive', icon: Archive, onClick: () => onFilterChange('archived') },
  ]

  // Remove "pinned" filter in grid view
  const filters = viewMode === 'grid'
    ? allFilters.filter(f => f.id !== 'pinned')
    : allFilters

  return (
    <div className="inline-flex items-center gap-1 p-1 bg-background border rounded-lg">
        {filters.map((filter) => {
          const Icon = filter.icon
          const isActive = filter.isActive !== undefined ? filter.isActive : activeFilter === filter.id

          return (
            <div
              key={filter.id}
              className="relative"
            >
              {isActive && (
                <motion.div
                  layoutId="activeFilter"
                  className="absolute inset-0 bg-stone-100 dark:bg-stone-800 rounded-md"
                  initial={false}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30
                  }}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={filter.onClick}
                className={`relative px-3 py-1.5 h-8 transition-all hover:bg-transparent ${
                  isActive
                    ? 'text-stone-900 dark:text-stone-100 font-semibold'
                    : 'text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5 mr-1.5" />
                <span className="text-xs">{filter.label}</span>
              </Button>
            </div>
          )
        })}

        {/* Separator between filters and select button (only show in grid view) */}
        {viewMode === 'grid' && onToggleSelectionMode && (
          <div className="w-px h-6 bg-stone-300 dark:bg-stone-700 mx-1" />
        )}

        {/* Select button - only show in grid view */}
        {viewMode === 'grid' && onToggleSelectionMode && (
          <div className="relative">
            {isSelectionMode && (
              <motion.div
                layoutId="selectModeActive"
                className="absolute inset-0 bg-blue-100 dark:bg-blue-900/30 rounded-md"
                initial={false}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 30
                }}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSelectionMode}
              className={`relative px-3 py-1.5 h-8 transition-all hover:bg-transparent ${
                isSelectionMode
                  ? 'text-blue-700 dark:text-blue-300 font-semibold'
                  : 'text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200'
              }`}
            >
              <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
              <span className="text-xs">{isSelectionMode ? 'Cancel' : 'Select'}</span>
            </Button>
          </div>
        )}
    </div>
  )
}